import { ChangeEvent, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import { useAddPatientForProvider } from '../../../api/hooks/patient/usePostSingleProviderPatient';
import BtnButton from "../../../components/shared/BtnButton";
import CheckWithLabelToggle from "../../../components/shared/CheckWithLabelToggle";
import ModalPortal from "../../../components/shared/ModalPortal";
import { CancelIcon } from "../../../svgs/CancelIcon";
import { Patient } from "../../../types/patientType";

const AddPatientFile = ({
  setShowAddPatientFile
}:
  {
    setShowAddPatientFile: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {

  const [fileName, setFileName] = useState<string>("");
  const [patientFileData, setPatientFileData] = useState<Partial<Patient>[]>([]);
  const fileTemplate = [
    [
      "First Name",
      "Last Name",
      "Email",
      "Date of Birth",
      "Phone Number",
      "State",
      "Insurance Name",
      "Insurance Number",
      "emailPatients"
    ]
  ];

  const [sendPatientEmail, setSendPatientEmail] = useState<boolean>(false);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setFileName(file?.name);
      const fileUrl = URL.createObjectURL(file);

      const response = await fetch(fileUrl);
      const text = await response.text();
      const lines = text.split("\n");
      const fileData = lines.map(line =>
        line.replaceAll('"', "").replace("\r", "").split(",")
      );

      const incorrectHeaderIndex = fileData[0].findIndex(
        (item, index) =>
          item.toLowerCase() !== fileTemplate[0][index].toLowerCase()
      );

      if (incorrectHeaderIndex >= 0) {
        setFileName("Error in file headers");
        return toast.error(
          `${fileTemplate[0][incorrectHeaderIndex]} is missing or is in the wrong position`,
          {
            type: "error"
          }
        );
      } else {
        const data = fileData
          .slice(1)
          .reduce((accumulator: Partial<Patient>[], currentValue: string[]) => {
            const patientKeys: (keyof Patient)[] = [
              "firstName",
              "lastName",
              "email",
              "dateOfBirth",
              "phoneNumber",
              "state",
              "insuranceName",
              "insuranceNumber"
            ];
            const newPatient: Partial<Patient> = currentValue.filter((item) => item).reduce(
              (prev, current, index) => ({
                ...prev,
                [patientKeys[index]]: current
              })
              , {});
            accumulator.push(newPatient);
            return accumulator;
          }, []);
        setPatientFileData(data);
      }
    }
  };

  const { addPatientForProvider, isPending } = useAddPatientForProvider()

  const handleSave = () => {
    addPatientForProvider({
      emailPatients: sendPatientEmail,
      data: patientFileData
    });
    setShowAddPatientFile(false);
  };

  return (
    <ModalPortal>
      <section
        style={{
          backgroundColor: "rgba(38, 38, 38, 0.75)",
          zIndex: 100,
          display: "flex",
          justifyContent: "center"
        }}
        className="w-screen h-screen fixed justify-center items-center top-0 bg-opacity-20 backdrop-blur-[5px]"
        onClick={() => setShowAddPatientFile(false)}
        data-testid='batch-add-patient-container'
      >
        <div
          className="min-w-min w-[65%] md:w-[60%]  bg-white px-5 py-10 flex flex-col gap-5 rounded-md "
          onClick={e => e.stopPropagation()}
        >
          <section className="flex justify-end" data-testid='close-x-btn'>
            <CancelIcon
              width={32}
              height={32}
              className="cursor-pointer"
              onClick={() => setShowAddPatientFile(false)}
            />
          </section>
          <section className="text-[18px] flex flex-col gap-4">
            <p data-testid='batch-upload-description'>
              Add multiple patients to your TempleHS account by uploading a CSV
              file
            </p>
            <p data-testid='batch-upload-sub-description'>
              Ensure that the columns in your file include our required fields:
              First Name, Last Name, Email, and State. Other fields are optional
            </p>
            <section className="w-[150px]" data-testid='download-sample-file'>
              <CSVLink
                data={fileTemplate}
                filename="patient-list.csv"
                uFEFF={true}
              >
                <BtnButton
                  variant="secondary"
                  label="DownloadTemplate"
                  size="medium"
                  additionalClassname="!w-[150px]"
                />
              </CSVLink>
            </section>
          </section>
          <div className="relative text-center border-[1px] border-[#7E954B] border-dashed p-4 cursor-pointer rounded-md w-[200px]">
            <h4 className="cursor-pointer" data-testid='click-to-upload-file-desc'>{fileName || "Click to upload file"}</h4>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="opacity-0 inset-0 absolute block h-full w-full"
              data-testid='upload-file-input'
            />
          </div>
          <section className="flex justify-between items-center w-full mt-5" data-testid='add-patient-file-btn-container'>
            <div className="flex items-center mt-5">
              <CheckWithLabelToggle
                label="Also email this patient ?"
                isSelected={sendPatientEmail}
                toggleSelection={() => setSendPatientEmail(prev => !prev)}
              />
            </div>
            <div className="flex items-center">
              <BtnButton
                label="Save"
                size="small"
                variant="primary"
                loading={isPending}
                disabled={isPending}
                onClick={handleSave}
              />
            </div>

          </section>
        </div>
      </section>
    </ModalPortal>
  );
};

export default AddPatientFile;
